'use client';

import { useState, useRef } from 'react';
import { Upload, Download, Image as ImageIcon, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { faviconGeneratorContent } from '../../data/favicon-generator-content';
import Link from 'next/link';

type FaviconSize = 16 | 32 | 48 | 64 | 96 | 128 | 192 | 256 | 512;

const DEFAULT_SIZES: FaviconSize[] = [16, 32, 48, 64, 96, 128, 192, 256, 512];
const FAVICON_SIZES = {
    'favicon.ico': [16, 32, 48],
    'favicon-16x16.png': [16],
    'favicon-32x32.png': [32],
    'apple-touch-icon.png': [180],
    'android-chrome-192x192.png': [192],
    'android-chrome-512x512.png': [512],
    'safari-pinned-tab.svg': [16],
} as const;

export default function FaviconGenerator() {
    const [originalImage, setOriginalImage] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedIcons, setGeneratedIcons] = useState<{ [key: string]: string }>({});
    const [selectedSizes, setSelectedSizes] = useState<FaviconSize[]>(DEFAULT_SIZES);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please upload a valid image file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setOriginalImage(event.target?.result as string);
            setFileName(file.name.split('.')[0] || 'favicon');
        };
        reader.readAsDataURL(file);
    };

    const generateFavicon = async () => {
        if (!originalImage) {
            toast.error('Please upload an image first');
            return;
        }

        setIsGenerating(true);
        setGeneratedIcons({});

        try {
            const img = new Image();
            img.src = originalImage;

            await new Promise((resolve) => {
                img.onload = resolve;
            });

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const generated: { [key: string]: string } = {};

            for (const [name, sizes] of Object.entries(FAVICON_SIZES)) {
                const size = Math.max(...sizes);
                canvas.width = size;
                canvas.height = size;

                // Draw the image centered and scaled to fit
                ctx.clearRect(0, 0, size, size);

                // Create a circular mask
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();

                // Draw the image
                const scale = Math.min(size / img.width, size / img.height);
                const x = (size - img.width * scale) / 2;
                const y = (size - img.height * scale) / 2;
                ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

                // Convert to blob and create object URL
                const blob = await new Promise<Blob | null>((resolve) => {
                    canvas.toBlob((blob) => resolve(blob), 'image/png');
                });

                if (blob) {
                    generated[name] = URL.createObjectURL(blob);
                }
            }

            setGeneratedIcons(generated);
            toast.success('Favicons generated successfully!');
        } catch (error) {
            console.error('Error generating favicons:', error);
            toast.error('Failed to generate favicons');
        } finally {
            setIsGenerating(false);
        }
    };

    const downloadAll = () => {
        Object.entries(generatedIcons).forEach(([name, url]) => {
            const link = document.createElement('a');
            link.href = url;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    };

    const downloadSingle = (url: string, name: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy to clipboard');
        }
    };

    const getHtmlCode = () => {
        return `<!-- Favicon HTML -->
<link rel="icon" href="/favicon.ico" sizes="any">
<link rel="icon" href="/favicon.svg" type="image/svg+xml">
<link rel="apple-touch-icon" href="/apple-touch-icon.png">
<link rel="manifest" href="/site.webmanifest">
`;
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6">
            <div className="mb-8">
                <ToolPageIntro title="Favicon Generator">
                Generate favicons from images with our free online tool. Create favicons in multiple sizes and formats for perfect website branding.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Upload Section */}
                <div className="space-y-6">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <Upload className="h-12 w-12 text-gray-400" />
                            <div className="text-sm text-gray-600">
                                <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                                >
                                    <span>Upload an image</span>
                                    <input
                                        id="file-upload"
                                        name="file-upload"
                                        type="file"
                                        className="sr-only"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        ref={fileInputRef}
                                    />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                        </div>

                        {originalImage && (
                            <div className="mt-4">
                                <div className="flex items-center justify-center">
                                    <img
                                        src={originalImage}
                                        alt="Preview"
                                        className="max-h-40 max-w-full rounded"
                                    />
                                </div>
                                <p className="mt-2 text-sm text-gray-600 truncate">
                                    {fileName}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <h3 className="font-medium text-gray-900 mb-3">Favicon Sizes</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {DEFAULT_SIZES.map((size) => (
                                <label key={size} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedSizes.includes(size)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedSizes([...selectedSizes, size]);
                                            } else {
                                                setSelectedSizes(selectedSizes.filter(s => s !== size));
                                            }
                                        }}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {size}x{size}px
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={generateFavicon}
                        disabled={!originalImage || isGenerating}
                        className={`w-full py-3 px-4 rounded-md text-white font-medium ${!originalImage || isGenerating
                            ? 'bg-blue-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                            } transition-colors`}
                    >
                        {isGenerating ? 'Generating...' : 'Generate Favicons'}
                    </button>
                </div>

                {/* Results Section */}
                <div>
                    {Object.keys(generatedIcons).length > 0 ? (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-gray-900">Generated Favicons</h2>
                                <button
                                    onClick={downloadAll}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download All
                                </button>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {Object.entries(generatedIcons).map(([name, url]) => (
                                    <div
                                        key={name}
                                        className="bg-white p-4 rounded-lg border border-gray-200 flex flex-col items-center"
                                    >
                                        <div className="w-20 h-20 flex items-center justify-center bg-gray-50 rounded mb-2">
                                            <img
                                                src={url}
                                                alt={name}
                                                className="max-w-full max-h-full"
                                            />
                                        </div>
                                        <span className="text-xs text-gray-500 mb-2">{name}</span>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => downloadSingle(url, name)}
                                                className="p-1 text-gray-500 hover:text-blue-600"
                                                title="Download"
                                            >
                                                <Download className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => copyToClipboard(url)}
                                                className="p-1 text-gray-500 hover:text-blue-600"
                                                title="Copy URL"
                                            >
                                                <Copy className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-6">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-medium text-gray-900">HTML Code</h3>
                                    <button
                                        onClick={() => copyToClipboard(getHtmlCode())}
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    >
                                        <Copy className="h-4 w-4 mr-1" />
                                        Copy
                                    </button>
                                </div>
                                <pre className="bg-gray-800 text-green-400 p-4 rounded-md overflow-x-auto text-sm">
                                    <code>{getHtmlCode()}</code>
                                </pre>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No favicons generated yet</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Upload an image and click "Generate Favicons" to get started.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={faviconGeneratorContent.title}
                    description={faviconGeneratorContent.description}
                    content={faviconGeneratorContent.content}
                    faqs={faviconGeneratorContent.faqs}
                    relatedTools={faviconGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}